import {ActionMenu, Button, CounterLabel, NavList} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Link} from '@github-ui/react-router'
import {clsx} from 'clsx'
import {useCallback, useLayoutEffect, useRef, useState, type RefObject} from 'react'
import {useHeaderTabsWithCounts} from './header-tabs/header-tabs'
import type {HeaderTab} from './header-tabs/types'
import type {HeaderRepository} from '../page-data/payloads/header'
import type {PullRequest} from '../types'
import type {NavigationUrls} from '../types/navigation-urls-types'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import styles from './MiniHeaderTabSwitcher.module.css'

export type MiniHeaderTabSwitcherProps = {
  activeRoute: PullRequestActiveRoute
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
  hidden?: boolean
  fallbackFocusRef?: RefObject<HTMLElement | null>
}

/**
 * Sticky-mini-header tab switcher. Renders an icon-only trigger whose icon mirrors the
 * active tab, opening a Primer NavList menu that lists every available PR tab in the same
 * order as the underline nav.
 */
export function MiniHeaderTabSwitcher({
  activeRoute,
  pullRequest,
  repository,
  urls,
  hidden = false,
  fallbackFocusRef,
}: MiniHeaderTabSwitcherProps) {
  const tabs = useHeaderTabsWithCounts({pullRequest, repository, urls})
  // Keep the count reader mounted while the transient menu follows scroll visibility.
  return hidden ? null : (
    <MiniHeaderTabSwitcherMenu tabs={tabs} activeRoute={activeRoute} fallbackFocusRef={fallbackFocusRef} />
  )
}

function MiniHeaderTabSwitcherMenu({
  tabs,
  activeRoute,
  fallbackFocusRef,
}: {
  tabs: HeaderTab[]
  activeRoute: PullRequestActiveRoute
  fallbackFocusRef?: RefObject<HTMLElement | null>
}) {
  const [open, setOpen] = useState(false)
  const closeMenu = useCallback(() => setOpen(false), [])
  const anchorRef = useRef<HTMLButtonElement>(null)
  const navigationRef = useRef<HTMLElement>(null)

  useLayoutEffect(
    () => () => {
      const activeElement = anchorRef.current?.ownerDocument.activeElement
      if (
        activeElement &&
        (anchorRef.current?.contains(activeElement) || navigationRef.current?.contains(activeElement))
      ) {
        const fallback = fallbackFocusRef?.current
        // Wait for Primer's overlay focus trap to be removed before restoring focus.
        queueMicrotask(() => {
          if (fallback?.isConnected) fallback.focus({preventScroll: true})
        })
      }
    },
    [fallbackFocusRef],
  )

  const activeTab = tabs.find(tab => tab.key === activeRoute) ?? tabs[0]
  if (!activeTab) return null

  const ActiveIcon = activeTab.icon

  return (
    <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
      <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={setOpen}>
        <ActionMenu.Anchor>
          <Button
            variant="invisible"
            size="medium"
            className={styles.trigger}
            aria-label={`Switch tab (current: ${activeTab.label})`}
            data-testid="mini-header-tab-switcher-trigger"
          >
            <span className={styles.triggerContent}>
              <ActiveIcon />
              <TriangleDownIcon size={12} />
            </span>
          </Button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="start">
          <NavList ref={navigationRef} aria-label="Pull request navigation (compact)">
            {tabs.map(tab => (
              <MiniHeaderTabSwitcherItem
                key={tab.key}
                tab={tab}
                isActive={tab.key === activeRoute}
                onNavigate={closeMenu}
              />
            ))}
          </NavList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </FeatureFlags>
  )
}

function MiniHeaderTabSwitcherItem({
  tab,
  isActive,
  onNavigate,
}: {
  tab: HeaderTab
  isActive: boolean
  onNavigate: () => void
}) {
  const Icon = tab.icon
  const ariaCurrent: 'page' | undefined = isActive ? 'page' : undefined
  const counter =
    tab.count !== undefined ? (
      <CounterLabel className={clsx(tab.key !== 'commits' && tab.countLoading ? styles.counterLoading : undefined)}>
        {`${tab.count}${tab.countLimitExceeded ? '+' : ''}`}
      </CounterLabel>
    ) : null

  // Don't set headerNavId because it's an anchor id used by non-React scroll
  // behavior and must remain unique on the page.
  if (tab.kind === 'router') {
    return (
      <NavList.Item as={Link} to={tab.to} aria-current={ariaCurrent} onClick={onNavigate}>
        <NavList.LeadingVisual>
          <Icon />
        </NavList.LeadingVisual>
        {tab.label}
        {counter && <NavList.TrailingVisual>{counter}</NavList.TrailingVisual>}
      </NavList.Item>
    )
  }

  return (
    <NavList.Item href={tab.href} data-prefetch={tab.prefetch} aria-current={ariaCurrent} onClick={onNavigate}>
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {tab.label}
      {counter && <NavList.TrailingVisual>{counter}</NavList.TrailingVisual>}
    </NavList.Item>
  )
}
